import { Controller } from "@hotwired/stimulus"

// Leaflet map controller for displaying branch locations
export default class extends Controller {
  static targets = ["container"]
  static values = {
    branches: Array,
    center: Array, // [lat, lng]
    zoom: Number
  }

  connect() {
    // Load Leaflet CSS and JS if not already loaded
    this.loadLeaflet().then(() => {
      this.initializeMap()
    })
  }

  disconnect() {
    if (this.map) {
      this.map.remove()
    }
  }

  async loadLeaflet() {
    // Check if Leaflet is already loaded
    if (window.L) {
      return Promise.resolve()
    }

    // Load Leaflet CSS
    if (!document.querySelector('link[href*="leaflet"]')) {
      const css = document.createElement('link')
      css.rel = 'stylesheet'
      css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
      css.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY='
      css.crossOrigin = ''
      document.head.appendChild(css)
    }

    // Load Leaflet JS
    return new Promise((resolve, reject) => {
      if (window.L) {
        resolve()
        return
      }

      const script = document.createElement('script')
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
      script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo='
      script.crossOrigin = ''
      script.onload = resolve
      script.onerror = reject
      document.head.appendChild(script)
    })
  }

  initializeMap() {
    if (!window.L) {
      console.error('Leaflet library not loaded')
      return
    }

    // Get initial center and zoom
    const center = this.hasCenterValue ? this.centerValue : [52.5200, 13.4050] // Default: Berlin
    const zoom = this.hasZoomValue ? this.zoomValue : 12

    // Initialize map
    this.map = L.map(this.containerTarget, {
      scrollWheelZoom: false,
      zoomControl: true
    }).setView(center, zoom)

    // Add tile layer (OpenStreetMap)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19
    }).addTo(this.map)

    // Add markers for branches
    if (this.hasBranchesValue && this.branchesValue.length > 0) {
      this.addBranchMarkers(this.branchesValue)
      this.fitMapToBranches(this.branchesValue)
    }

    // Enable scroll zoom after click
    this.map.on('click', () => {
      this.map.scrollWheelZoom.enable()
    })

    // Disable scroll zoom when mouse leaves
    this.map.on('mouseout', () => {
      this.map.scrollWheelZoom.disable()
    })
  }

  addBranchMarkers(branches) {
    const markers = []

    branches.forEach(branch => {
      if (branch.latitude && branch.longitude) {
        // Create custom icon
        const icon = L.divIcon({
          className: 'custom-marker',
          html: `
            <div class="marker-pin">
              <div class="marker-icon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" width="20" height="20">
                  <path d="M12 0C7.589 0 4 3.589 4 8c0 5.5 8 16 8 16s8-10.5 8-16c0-4.411-3.589-8-8-8zm0 11.5c-1.933 0-3.5-1.567-3.5-3.5S10.067 4.5 12 4.5s3.5 1.567 3.5 3.5S13.933 11.5 12 11.5z"/>
                </svg>
              </div>
            </div>
          `,
          iconSize: [40, 40],
          iconAnchor: [20, 40],
          popupAnchor: [0, -40]
        })

        // Create marker
        const marker = L.marker([branch.latitude, branch.longitude], { icon })
          .addTo(this.map)

        // Create popup content
        const popupContent = this.createPopupContent(branch)
        marker.bindPopup(popupContent, {
          maxWidth: 300,
          className: 'branch-popup'
        })

        markers.push(marker)
      }
    })

    // Create marker cluster if many markers (optional enhancement)
    if (markers.length > 50 && window.L.markerClusterGroup) {
      const markerCluster = L.markerClusterGroup()
      markers.forEach(marker => markerCluster.addLayer(marker))
      this.map.addLayer(markerCluster)
    }
  }

  createPopupContent(branch) {
    const rating = branch.rating ? `⭐ ${branch.rating.toFixed(1)}` : ''
    const ratingCount = branch.rating_count ? `(${branch.rating_count})` : ''
    const image = branch.primary_image || branch.image_url

    return `
      <div class="branch-popup-content">
        ${image ? `
          <div class="popup-image" style="width: 100%; height: 120px; overflow: hidden; border-radius: 0.5rem; margin-bottom: 0.75rem;">
            <img src="${image}" alt="${branch.display_name}" style="width: 100%; height: 100%; object-fit: cover;">
          </div>
        ` : ''}
        <h3 style="font-size: 1.125rem; font-weight: 700; color: #1f2937; margin-bottom: 0.25rem;">
          ${branch.display_name || branch.name}
        </h3>
        <p style="font-size: 0.875rem; color: #6b7280; margin-bottom: 0.5rem;">
          ${branch.merchant_name || ''}
        </p>
        ${rating ? `
          <div style="display: flex; align-items: center; gap: 0.25rem; margin-bottom: 0.5rem; font-size: 0.875rem;">
            <span>${rating}</span>
            <span style="color: #9ca3af;">${ratingCount}</span>
          </div>
        ` : ''}
        <p style="font-size: 0.875rem; color: #6b7280; margin-bottom: 0.75rem;">
          📍 ${branch.city || ''}${branch.address ? `<br/>${branch.address}` : ''}
        </p>
        ${branch.url ? `
          <a href="${branch.url}"
             style="display: inline-block; padding: 0.5rem 1rem; background-color: #4f46e5; color: white; font-weight: 600; border-radius: 0.5rem; text-decoration: none; font-size: 0.875rem;">
            ${this.translateViewDetails()}
          </a>
        ` : ''}
      </div>
    `
  }

  fitMapToBranches(branches) {
    const validBranches = branches.filter(b => b.latitude && b.longitude)

    if (validBranches.length === 0) return

    if (validBranches.length === 1) {
      // Single branch: center on it
      const branch = validBranches[0]
      this.map.setView([branch.latitude, branch.longitude], 15)
    } else {
      // Multiple branches: fit bounds
      const bounds = L.latLngBounds(
        validBranches.map(b => [b.latitude, b.longitude])
      )
      this.map.fitBounds(bounds, {
        padding: [50, 50],
        maxZoom: 15
      })
    }
  }

  translateViewDetails() {
    // Check current locale from HTML lang attribute or default to 'en'
    const locale = document.documentElement.lang || 'en'
    return locale === 'de' ? 'Details anzeigen' : 'View details'
  }

  // Method to update branches dynamically (e.g., after search)
  updateBranches(branches) {
    // Clear existing markers
    this.map.eachLayer(layer => {
      if (layer instanceof L.Marker) {
        this.map.removeLayer(layer)
      }
    })

    // Add new markers
    if (branches && branches.length > 0) {
      this.addBranchMarkers(branches)
      this.fitMapToBranches(branches)
    }
  }
}
